import React, { useState } from 'react';
import {
  Calendar, MapPin, Users, Tag,
  Globe, Link, Copy, ExternalLink, MessageSquare,
  Clock, UserCheck, CreditCard, Users as UsersIcon,
  Building, Award, Target, Sparkles,
  Check, CalendarDays, MapPinned,
  WifiOff, GraduationCap, Briefcase, Info,
  Hash, Laptop, Edit, Layers, ChevronRight,
  ChevronDown, IndianRupee, Percent, Boxes,
  Network, GitBranch, FolderTree, LayoutGrid
} from 'lucide-react';

const EventViewModal = ({
  isOpen,
  event,
  onClose,
  onEdit,
  onShare,
  eventTypes,
  eventModes,
  participantTypes,
  eventStatuses,
  userRole
}) => {
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedSubEvents, setExpandedSubEvents] = useState([]);

  if (!isOpen || !event) return null;

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return { date: 'N/A', time: 'N/A' };
    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return { date: 'N/A', time: 'N/A' };

      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      let dateStr;
      if (date.toDateString() === today.toDateString()) {
        dateStr = 'Today';
      } else if (date.toDateString() === tomorrow.toDateString()) {
        dateStr = 'Tomorrow';
      } else {
        dateStr = date.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
      }

      return {
        date: dateStr,
        time: date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        }),
        full: date.toLocaleString(),
        isToday: date.toDateString() === today.toDateString(),
        isTomorrow: date.toDateString() === tomorrow.toDateString()
      };
    } catch {
      return { date: 'N/A', time: 'N/A' };
    }
  };

  const getStatusInfo = (statusId) => {
    const status = eventStatuses?.find(s => s.Status_ID === Number(statusId));

    const statusConfig = {
      1: {
        name: 'Active',
        bg: 'bg-gradient-to-r from-emerald-500 to-green-500',
        lightBg: 'bg-emerald-50',
        border: 'border-emerald-200',
        text: 'text-emerald-700',
        darkText: 'text-emerald-800',
        icon: <Sparkles size={14} className="animate-pulse" />,
        badge: 'bg-emerald-500 text-white'
      },
      2: {
        name: 'Upcoming',
        bg: 'bg-gradient-to-r from-blue-500 to-indigo-500',
        lightBg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-700',
        darkText: 'text-blue-800',
        icon: <CalendarDays size={14} />,
        badge: 'bg-blue-500 text-white'
      },
      3: {
        name: 'Completed',
        bg: 'bg-gradient-to-r from-gray-500 to-slate-500',
        lightBg: 'bg-gray-50',
        border: 'border-gray-200',
        text: 'text-gray-700',
        darkText: 'text-gray-800',
        icon: <Award size={14} />,
        badge: 'bg-gray-500 text-white'
      },
      4: {
        name: 'Cancelled',
        bg: 'bg-gradient-to-r from-red-500 to-rose-500',
        lightBg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-700',
        darkText: 'text-red-800',
        icon: <WifiOff size={14} />,
        badge: 'bg-red-500 text-white'
      }
    };

    const config = statusConfig[Number(statusId)] || {
      name: status?.name || `Status ${statusId}`,
      bg: 'bg-gradient-to-r from-blue-500 to-indigo-500',
      lightBg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-700',
      darkText: 'text-blue-800',
      icon: <Info size={14} />,
      badge: 'bg-blue-500 text-white'
    };

    return {
      ...config,
      name: status?.Status_Name || config.name
    };
  };

  const getEventTypeInfo = (typeId) => {
    const type = eventTypes?.find(t => t.id === typeId);

    const typeColors = {
      1: { bg: 'bg-gradient-to-r from-blue-500 to-blue-500', lightBg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: '🎤', iconComponent: <UsersIcon size={14} /> },
      2: { bg: 'bg-gradient-to-r from-blue-500 to-blue-500', lightBg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', icon: '🎓', iconComponent: <GraduationCap size={14} /> },
      3: { bg: 'bg-gradient-to-r from-cyan-500 to-blue-500', lightBg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', icon: '💼', iconComponent: <Briefcase size={14} /> },
      4: { bg: 'bg-gradient-to-r from-emerald-500 to-teal-500', lightBg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: '👥', iconComponent: <Users size={14} /> },
      5: { bg: 'bg-gradient-to-r from-amber-500 to-orange-500', lightBg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: '🎵', iconComponent: <Award size={14} /> },
      6: { bg: 'bg-gradient-to-r from-rose-500 to-pink-500', lightBg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', icon: '🏆', iconComponent: <Target size={14} /> },
      7: { bg: 'bg-gradient-to-r from-violet-500 to-purple-500', lightBg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', icon: '🎨', iconComponent: <Award size={14} /> },
      8: { bg: 'bg-gradient-to-r from-red-500 to-pink-500', lightBg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: '❤️', iconComponent: <Award size={14} /> },
      9: { bg: 'bg-gradient-to-r from-yellow-500 to-amber-500', lightBg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', icon: '🍔', iconComponent: <Award size={14} /> },
      10: { bg: 'bg-gradient-to-r from-purple-500 to-pink-500', lightBg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', icon: '🎮', iconComponent: <Award size={14} /> },
    };

    const defaultColor = {
      bg: 'bg-gradient-to-r from-blue-500 to-indigo-500',
      lightBg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
      icon: '🎭',
      iconComponent: <Calendar size={14} />
    };

    const color = typeColors[Number(typeId)] || defaultColor;

    return {
      name: type?.Event_Type_Name || type?.name || 'Event',
      ...color
    };
  };

  const getEventModeInfo = (modeId) => {
    const mode = eventModes?.find(m => m.id === modeId);

    const modeColors = {
      1: {
        bg: 'bg-gradient-to-r from-blue-500 to-indigo-500',
        lightBg: 'bg-blue-50',
        text: 'text-blue-700',
        border: 'border-blue-200',
        icon: <Laptop size={14} />,
        label: 'Online'
      },
      2: {
        bg: 'bg-gradient-to-r from-indigo-500 to-purple-500',
        lightBg: 'bg-indigo-50',
        text: 'text-indigo-700',
        border: 'border-indigo-200',
        icon: <Building size={14} />,
        label: 'In-person'
      },
      3: {
        bg: 'bg-gradient-to-r from-cyan-500 to-teal-500',
        lightBg: 'bg-cyan-50',
        text: 'text-cyan-700',
        border: 'border-cyan-200',
        icon: <Globe size={14} />,
        label: 'Hybrid'
      }
    };

    const defaultColor = {
      bg: 'bg-gradient-to-r from-blue-500 to-indigo-500',
      lightBg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
      icon: <MapPin size={14} />,
      label: 'Unknown'
    };

    const color = modeColors[Number(modeId)] || defaultColor;

    return {
      name: mode?.Event_Mode_Name || mode?.name || color.label,
      ...color
    };
  };

  const getParticipantTypeName = (typeId) => {
    const type = participantTypes?.find(type => type.id === typeId);
    return type?.Participant_Type_Name || type?.name || 'Participant';
  };

  const calculateEventStats = () => {
    let totalCategories = 0;
    let totalCapacity = 0;
    let totalAmount = 0;

    if (event.HasSubEvents === '1' && event.SubEventDetails?.length > 0) {
      event.SubEventDetails.forEach(subEvent => {
        if (subEvent.Participants) {
          totalCategories += subEvent.Participants.length;
          subEvent.Participants.forEach(p => {
            totalCapacity += Number(p.MaxParticipants) || 0;
            totalAmount += Number(p.TotAmt) || 0;
          });
        }
      });
    } else if (event.Participants) {
      totalCategories = event.Participants.length;
      totalCapacity = event.Participants.reduce((sum, p) => sum + (Number(p.Max_participant) || 0), 0);
      totalAmount = event.Participants.reduce((sum, p) => sum + (Number(p.TotAmt) || 0), 0);
    }

    return { totalCategories, totalCapacity, totalAmount };
  };

  const toggleSubEvent = (subEventId) => {
    setExpandedSubEvents(prev =>
      prev.includes(subEventId)
        ? prev.filter(id => id !== subEventId)
        : [...prev, subEventId]
    );
  };

  const stats = calculateEventStats();
  const statusInfo = getStatusInfo(event.Event_status);
  const typeInfo = getEventTypeInfo(event.Event_type);
  const modeInfo = getEventModeInfo(event.Event_mode);
  const startDateTime = formatDateTime(event.Start_datetime);
  const endDateTime = formatDateTime(event.End_datetime);
  const hasSubEvents = event.HasSubEvents === '1' && event.SubEventDetails?.length > 0;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(event.Registration_Link || `${window.location.origin}/event/${event.Event_Id}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      onShare?.(event);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const isCompleted = new Date(event.End_datetime) < new Date();

  const renderParticipantsGrid = (participants, parentId = null) => {
    if (!participants || participants.length === 0) {
      return (
        <div className="bg-gray-50 rounded-xl p-6 text-center border-2 border-dashed border-gray-300">
          <div className="flex flex-col items-center gap-2">
            <div className="p-3 bg-gray-200 rounded-full">
              <Users size={24} className="text-gray-400" />
            </div>
            <h4 className="text-sm font-semibold text-gray-700">No Participants</h4>
            <p className="text-xs text-gray-500">No participant categories defined.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {participants.map((participant, index) => {
          // Handle both regular event participants and sub-event participants
          const participantId = participant.Participant_TypeId || participant.Id;
          const typeName = getParticipantTypeName(participantId);

          // Handle different field names
          const maxParticipants = participant.MaxParticipants ?? participant.Max_participant ?? 0;
          const amount = participant.Amount ?? participant.WithoutGst_Amt ?? 0;
          const TotAmt = participant.TotAmt ?? participant.TotAmt ?? 0;
          const gstPer = participant.Gst_Per ?? participant.Gst_Percents ?? 0;
          const gstAmt = participant.Gst_Amt ?? 0;
          const isLimited = participant.IsLimited !== 0 && participant.IsLimited !== false;

          const colors = [
            'from-blue-500 to-indigo-500',
            'from-indigo-500 to-purple-500',
            'from-purple-500 to-pink-500',
            'from-cyan-500 to-blue-500',
            'from-emerald-500 to-teal-500',
            'from-amber-500 to-orange-500'
          ];
          const gradient = colors[index % colors.length];

          return (
            <div key={index} className="group bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              {/* Card Header with Gradient */}
              <div className={`bg-gradient-to-r ${gradient} px-4 py-3 relative overflow-hidden`}>
                <div className="absolute inset-0 bg-white/10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                <div className="flex items-center justify-between relative">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                      <UsersIcon size={14} className="text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white text-sm">{typeName || 'Participant'}</h4>
                    </div>
                  </div>
                  <div className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                    <span className="text-xs font-medium text-white">ID: {participantId}</span>
                  </div>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-4">
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="bg-gray-50 rounded-lg p-3 group-hover:bg-gray-100 transition-colors">
                    <p className="text-xs text-gray-500 mb-1">Capacity</p>
                    <div className="flex items-center gap-1">
                      <Users size={16} className="text-gray-400" />
                      <p className="text-lg font-bold text-gray-900">
                        {maxParticipants == 0 ? '∞' : maxParticipants}
                      </p>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 group-hover:bg-gray-100 transition-colors">
                    <p className="text-xs text-gray-500 mb-1">Base Price</p>
                    <div className="flex items-center gap-1">
                      <IndianRupee size={16} className="text-gray-400" />
                      <p className="text-lg font-bold text-gray-900">₹{amount || 0}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-100">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-1">
                      <Percent size={14} className="text-blue-600" />
                      <span className="text-xs font-medium text-gray-700">GST {gstPer}%</span>
                    </div>
                    <span className="px-2 py-1 bg-white rounded-lg text-xs font-medium text-blue-700 shadow-sm">
                      +₹{gstAmt || 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-blue-200">
                    <span className="text-xs font-medium text-gray-600">Total (incl. GST)</span>
                    <span className="text-lg font-bold text-blue-600">₹{TotAmt}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50 backdrop-blur-md">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden border border-gray-200 animate-fadeIn">

        {/* Header - Dynamic Gradient based on event type */}
        <div className={`bg-gradient-to-r ${typeInfo.bg} px-6 py-4 relative overflow-hidden`}>
          <div className="absolute inset-0 bg-white/10 transform -skew-x-12 -translate-x-1/2"></div>
          <div className="relative">
            {/* Title Section */}
            <div className="mt-3">
              <h2 className="text-xl font-bold text-white mb-1 tracking-tight line-clamp-1">{event.Event_Name}</h2>
              <div className="flex items-center gap-2 text-white/90">
                <GraduationCap size={14} className="opacity-80" />
                <span className="text-xs font-medium">{event.Inst_Name || 'Institution'}</span>
                {event.Div_Name && (
                  <>
                    <span className="text-white/50">•</span>
                    <span className="text-xs">{event.Div_Name}</span>
                  </>
                )}
              </div>
            </div>

            {/* Tags Row */}
            <div className="flex flex-wrap gap-1.5 mt-3">
              <span className="px-2 py-1 rounded-lg text-xs font-medium bg-white/20 backdrop-blur-sm text-white border border-white/30 flex items-center gap-1">
                {typeInfo.iconComponent}
                {typeInfo.name}
              </span>
              <span className="px-2 py-1 rounded-lg text-xs font-medium bg-white/20 backdrop-blur-sm text-white border border-white/30 flex items-center gap-1">
                {modeInfo.icon}
                {modeInfo.name}
              </span>
              {event.IsGroupEvent === 'Y' && (
                <span className="px-2 py-1 rounded-lg text-xs font-medium bg-white/20 backdrop-blur-sm text-white border border-white/30 flex items-center gap-1">
                  <UsersIcon size={12} />
                  Group Event
                </span>
              )}
              {hasSubEvents && (
                <span className="px-2 py-1 rounded-lg text-xs font-medium bg-white/20 backdrop-blur-sm text-white border border-white/30 flex items-center gap-1">
                  <Layers size={12} />
                  {event.SubEventDetails.length} Sub-events
                </span>
              )}
              <span className="px-2 py-1 rounded-lg text-xs font-medium bg-white/20 backdrop-blur-sm text-white border border-white/30">
                {event.Event_Sname}
              </span>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="border-b border-gray-200 bg-gray-50/80 px-6">
          <div className="flex gap-5">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-3 px-1 text-xs font-medium border-b-2 transition-all relative ${activeTab === 'overview'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Overview
              {activeTab === 'overview' && (
                <span className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 animate-slideIn"></span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('participants')}
              className={`py-3 px-1 text-xs font-medium border-b-2 transition-all relative ${activeTab === 'participants'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              {hasSubEvents ? 'Sub-events & Pricing' : 'Participants & Pricing'}
              {stats.totalCategories > 0 && (
                <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded-full text-[10px]">
                  {stats.totalCategories}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('details')}
              className={`py-3 px-1 text-xs font-medium border-b-2 transition-all relative ${activeTab === 'details'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Additional Details
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto p-6" style={{ maxHeight: 'calc(90vh - 280px)' }}>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Quick Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100 hover:shadow-md transition-all">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 ${typeInfo.bg} rounded-lg shadow-sm`}>
                      <Calendar size={16} className="text-white" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Date</p>
                      <p className="font-semibold text-gray-900 text-sm">{startDateTime.date}</p>
                      <p className="text-xs text-gray-600">{startDateTime.time}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100 hover:shadow-md transition-all">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 ${modeInfo.bg} rounded-lg shadow-sm`}>
                      <MapPinned size={16} className="text-white" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Venue</p>
                      <p className="font-semibold text-gray-900 text-sm truncate max-w-[120px]">{event.Event_Place || 'TBA'}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-100 hover:shadow-md transition-all">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg shadow-sm">
                      <Users size={16} className="text-white" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Categories</p>
                      <p className="font-semibold text-gray-900 text-sm">{stats.totalCategories}</p>
                      <p className="text-xs text-gray-600">Total capacity: {stats.totalCapacity || '∞'}</p>
                    </div>
                  </div>
                </div>

                {/* <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-100 hover:shadow-md transition-all">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg shadow-sm">
                      <CreditCard size={16} className="text-white" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</p>
                      <p className="font-semibold text-gray-900 text-sm">₹{stats.totalAmount}</p>
                      <p className="text-xs text-gray-600">Total potential</p>
                    </div>
                  </div>
                </div> */}
              </div>

              {/* Description */}
              {event.Event_Description && (
                <div className="bg-white rounded-xl p-5 border border-gray-200 hover:shadow-md transition-all">
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`p-1.5 ${typeInfo.bg} rounded-lg`}>
                      <MessageSquare size={14} className="text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm">About This Event</h3>
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed pl-1 whitespace-pre-wrap">
                    {event.Event_Description}
                  </p>
                </div>
              )}

              {/* Schedule & Venue */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl p-5 border border-gray-200 hover:shadow-md transition-all">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-1.5 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg">
                      <Clock size={14} className="text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm">Event Schedule</h3>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-2 hover:bg-amber-50 rounded-lg transition-colors">
                      <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Clock size={14} className="text-amber-600" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500">Starts</p>
                        <p className="font-semibold text-gray-900 text-sm">{startDateTime.date}</p>
                        <p className="text-xs text-gray-600">{startDateTime.time}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-2 hover:bg-amber-50 rounded-lg transition-colors">
                      <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                        <Clock size={14} className="text-amber-600" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500">Ends</p>
                        <p className="font-semibold text-gray-900 text-sm">{endDateTime.date}</p>
                        <p className="text-xs text-gray-600">{endDateTime.time}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-5 border border-gray-200 hover:shadow-md transition-all">
                  <div className="flex items-center gap-2 mb-4">
                    <div className={`p-1.5 ${modeInfo.bg} rounded-lg`}>
                      <MapPin size={14} className="text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm">Venue Information</h3>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-2 hover:bg-indigo-50 rounded-lg transition-colors">
                      <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <Building size={14} className="text-indigo-600" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500">Location</p>
                        <p className="font-semibold text-gray-900 text-sm">{event.Event_Place || 'Not specified'}</p>
                        <p className="text-xs text-gray-600">{modeInfo.name}</p>
                      </div>
                    </div>

                    {event.GPS_coordinates && (
                      <div className="flex items-start gap-3 p-2 hover:bg-purple-50 rounded-lg transition-colors">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Target size={14} className="text-purple-600" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">GPS</p>
                          <p className="font-medium text-gray-900 text-xs">{event.GPS_coordinates}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Participants & Pricing Tab */}
          {activeTab === 'participants' && (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`p-1.5 ${typeInfo.bg} rounded-lg`}>
                    {hasSubEvents ? <Layers size={16} className="text-white" /> : <UserCheck size={16} className="text-white" />}
                  </div>
                  <h3 className="text-base font-bold text-gray-900">
                    {hasSubEvents ? 'Sub-events & Categories' : 'Participant Categories'}
                  </h3>
                </div>
                <div className="px-3 py-1.5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                  <span className="text-xs font-medium text-blue-700">
                    {stats.totalCategories} Categories {hasSubEvents ? `across ${event.SubEventDetails.length} sub-events` : ''}
                  </span>
                </div>
              </div>

              {/* Dynamic Content based on SubEvents */}
              {hasSubEvents ? (
                <div className="space-y-4">
                  {event.SubEventDetails.map((subEvent, subIndex) => {
                    const isExpanded = expandedSubEvents.includes(subEvent.SubEventId);
                    const subEventColors = [
                      'from-blue-500 to-indigo-500',
                      'from-indigo-500 to-purple-500',
                      'from-purple-500 to-pink-500',
                      'from-cyan-500 to-blue-500'
                    ];
                    const gradient = subEventColors[subIndex % subEventColors.length];

                    return (
                      <div key={subEvent.SubEventId} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-lg transition-all">
                        {/* Sub-event Header - Clickable */}
                        <div
                          onClick={() => toggleSubEvent(subEvent.SubEventId)}
                          className={`bg-gradient-to-r ${gradient} px-5 py-4 cursor-pointer hover:opacity-90 transition-opacity relative overflow-hidden group`}
                        >
                          <div className="absolute inset-0 bg-white/10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                          <div className="flex items-center justify-between relative">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                                <FolderTree size={16} className="text-white" />
                              </div>
                              <div>
                                <h4 className="font-semibold text-white">{subEvent.Name}</h4>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs text-white/80">ID: {subEvent.SubEvent_Id}</span>
                                  {subEvent.ShortName && (
                                    <>
                                      <span className="text-white/40">•</span>
                                      <span className="text-xs text-white/80">{subEvent.ShortName}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-2">
                                <div className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                                  <span className="text-xs font-medium text-white">
                                    {subEvent.Participants?.length || 0} categories
                                  </span>
                                </div>
                                <div className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                                  <span className="text-xs font-medium text-white">
                                    ₹{subEvent.Participants?.reduce((sum, p) => sum + (Number(p.TotAmt) || 0), 0) || 0}
                                  </span>
                                </div>
                              </div>
                              {isExpanded ? (
                                <ChevronDown size={20} className="text-white transition-transform" />
                              ) : (
                                <ChevronRight size={20} className="text-white transition-transform" />
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Sub-event Participants - Collapsible */}
                        {isExpanded && (
                          <div className="p-5 bg-gray-50 border-t border-gray-200">
                            {renderParticipantsGrid(subEvent.Participants, subEvent.SubEventId)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                // Regular participants grid
                renderParticipantsGrid(event.Participants)
              )}

              {/* Payment Schedule */}
              {(event.Pay_Start_datetime || event.Pay_End_datetime) && (
                <div className="mt-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-5 border border-emerald-200">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg shadow-sm">
                      <CreditCard size={14} className="text-white" />
                    </div>
                    <h3 className="text-sm font-bold text-gray-900">Payment Schedule</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {event.Pay_Start_datetime && (
                      <div className="bg-white rounded-lg p-4 border border-emerald-200 hover:shadow-md transition-all">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                            <Calendar size={16} className="text-emerald-600" />
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500">Opens</p>
                            <p className="font-semibold text-gray-900 text-sm">{formatDateTime(event.Pay_Start_datetime).date}</p>
                            <p className="text-xs text-gray-600">{formatDateTime(event.Pay_Start_datetime).time}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {event.Pay_End_datetime && (
                      <div className="bg-white rounded-lg p-4 border border-emerald-200 hover:shadow-md transition-all">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                            <Calendar size={16} className="text-emerald-600" />
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500">Closes</p>
                            <p className="font-semibold text-gray-900 text-sm">{formatDateTime(event.Pay_End_datetime).date}</p>
                            <p className="text-xs text-gray-600">{formatDateTime(event.Pay_End_datetime).time}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Additional Details Tab */}
          {activeTab === 'details' && (
            <div className="space-y-5">
              {/* Registration Link */}
              {event.Registration_Link && userRole == "Administrator" && !isCompleted && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`p-1.5 ${typeInfo.bg} rounded-lg`}>
                        <Link size={14} className="text-white" />
                      </div>
                      <h3 className="text-sm font-bold text-gray-900">Registration Link</h3>
                    </div>
                    <button
                      onClick={handleCopyLink}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-blue-200 text-blue-700 hover:bg-blue-50 text-xs font-medium rounded-lg transition-all hover:shadow-md"
                    >
                      {copied ? (
                        <>
                          <Check size={14} className="text-green-600" />
                          <span>Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy size={14} />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                  <div className="bg-white border border-blue-200 rounded-lg p-3 hover:shadow-md transition-all">
                    <a
                      href={event.Registration_Link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 flex items-center gap-1.5 text-xs break-all"
                    >
                      {event.Registration_Link}
                      <ExternalLink size={12} className="flex-shrink-0" />
                    </a>
                  </div>
                </div>
              )}

              {/* Important Message */}
              {event.Message && (
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-5 border border-amber-200">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-1.5 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg shadow-sm">
                      <MessageSquare size={14} className="text-white" />
                    </div>
                    <h3 className="text-sm font-bold text-gray-900">Important Message</h3>
                  </div>
                  <div className="bg-white/80 border border-amber-200 rounded-lg p-3 hover:shadow-md transition-all">
                    <p className="text-amber-800 text-sm leading-relaxed">
                      {event.Message}
                    </p>
                  </div>
                </div>
              )}

              {/* Metadata Grid */}
              <div className="bg-white rounded-xl p-5 border border-gray-200 hover:shadow-md transition-all">
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-1.5 bg-gradient-to-r from-gray-600 to-slate-600 rounded-lg">
                    <Info size={14} className="text-white" />
                  </div>
                  <h3 className="text-sm font-bold text-gray-900">Event Metadata</h3>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors">
                    <p className="text-xs text-gray-500 mb-1">Event ID</p>
                    <div className="flex items-center gap-1">
                      <Hash size={14} className="text-gray-400" />
                      <span className="text-sm font-semibold text-gray-900">{event.Event_Id}</span>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors">
                    <p className="text-xs text-gray-500 mb-1">Organizer</p>
                    <div className="flex items-center gap-1">
                      <Building size={14} className="text-gray-400" />
                      <span className="text-sm font-semibold text-gray-900">{event.Organizer_id}</span>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors">
                    <p className="text-xs text-gray-500 mb-1">Short Name</p>
                    <div className="flex items-center gap-1">
                      <Tag size={14} className="text-gray-400" />
                      <span className="text-sm font-semibold text-gray-900">{event.Event_Sname}</span>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors">
                    <p className="text-xs text-gray-500 mb-1">Event Type</p>
                    <div className="flex items-center gap-1">
                      <LayoutGrid size={14} className="text-gray-400" />
                      <span className="text-sm font-semibold text-gray-900">{typeInfo.name}</span>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors">
                    <p className="text-xs text-gray-500 mb-1">Event Mode</p>
                    <div className="flex items-center gap-1">
                      <Globe size={14} className="text-gray-400" />
                      <span className="text-sm font-semibold text-gray-900">{modeInfo.name}</span>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors">
                    <p className="text-xs text-gray-500 mb-1">Group Event</p>
                    <span className={`text-sm font-semibold ${event.IsGroupEvent === 'Y' ? 'text-green-600' : 'text-gray-500'}`}>
                      {event.IsGroupEvent === 'Y' ? 'Yes' : 'No'}
                    </span>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors">
                    <p className="text-xs text-gray-500 mb-1">Has Sub-events</p>
                    <span className={`text-sm font-semibold ${hasSubEvents ? 'text-blue-600' : 'text-gray-500'}`}>
                      {hasSubEvents ? `Yes (${event.SubEventDetails.length})` : 'No'}
                    </span>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors">
                    <p className="text-xs text-gray-500 mb-1">Dept ID</p>
                    <span className="text-sm font-semibold text-gray-900">{event.Dept_id || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 ${statusInfo.bg} rounded-full animate-pulse`}></div>
            <span className="text-xs text-gray-600">
              {statusInfo.name} • Updated: {formatDateTime(event.Updated_at || new Date()).date}
            </span>
          </div>

          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-xs text-gray-700 hover:text-gray-900 font-medium bg-white border border-gray-300 hover:border-gray-400 rounded-lg transition-all hover:shadow-md"
            >
              Close
            </button>
            {onEdit && userRole == "Administrator" && !isCompleted && (
              <button
                onClick={() => onEdit(event)}
                className={`px-4 py-2 text-xs ${typeInfo.bg} text-white font-medium rounded-lg transition-all shadow-sm hover:shadow-lg flex items-center gap-1.5`}
              >
                <Edit size={14} />
                Edit Event
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventViewModal;